import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { fetchAlertsForDevices, selectDevices } from '../../reducers/devices'
import { useAppDispatch } from '../../reducers/store'
import { DeviceAlertRow, DeviceOverviewInformation } from '../../types/DeviceTypes'
import PageSkeleton from '../skeleton/PageSkeleton'
import IntelDataGrid from '../data-grid/IntelDataGrid'
import { deviceAlertColumns } from './DeviceAlertColumns'
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../../strings/api-consts'
import { useSelector } from 'react-redux'
import { formatUtcTimestamp, timeSince } from '../../utils/dates'
import { AlertType } from '../../types/AlertTypes'
import { Button, Tooltip } from '@radix-ui/themes'
import {
  IconAlertHexagon,
  IconBrandAndroid,
  IconBrandApple,
  IconCalendarCheck,
  IconCode,
  IconDeviceMobile,
  IconDownload,
  IconFaceId,
  IconHandStop,
  IconInfoCircle,
  IconLineScan,
  IconLockOpen2,
  IconMessage2Cancel,
  IconPasswordMobilePhone,
  IconShield,
  IconUsb,
  IconUser,
  IconVersions,
} from '@tabler/icons-react'
import InfoItem from './InfoItem'
import { selectUser } from '../../reducers/users'
import { inPov } from '../../utils/session'

interface DeviceOverviewSummaryProps {
  fetchedDetails: DeviceOverviewInformation | undefined
  setPopoutAlertData: (data: { alertId: number; scanId: string; alertType: AlertType }) => void
  setShowAlertPopout: (show: boolean) => void
  setShowSimulateAlertModal: (show: boolean) => void
  setAlertSortOrder: (order: string) => void
  setAlertSortColumn: (column: string) => void
  sortAlertOrder: string
  sortAlertColumn: string
}

const DeviceOverviewSummary: React.FC<DeviceOverviewSummaryProps> = (props) => {
  const {
    fetchedDetails,
    setPopoutAlertData,
    setShowAlertPopout,
    setShowSimulateAlertModal,
    setAlertSortOrder: setSortOrder,
    setAlertSortColumn: setSortColumn,
    sortAlertOrder: sortOrder,
    sortAlertColumn: sortColumn,
  } = props
  const di = useAppDispatch()
  const { deviceId } = useParams()
  const navigate = useNavigate()
  const { session } = useSelector(selectUser)
  const { deviceAlerts } = useSelector(selectDevices)
  const [mainDataLoading, setMainDataLoading] = useState<boolean>(true)
  const [dataLoading, setDataLoading] = useState<boolean>(true)
  const defaultSortOrder = 'desc'
  const defaultSortColumn = 'lastSeen'

  const sortBy = (column: string) => {
    if (sortColumn === column) {
      if (sortOrder === 'desc' && column !== defaultSortColumn) {
        //  Clear Sort
        setSortColumn(defaultSortColumn)
        setSortOrder(defaultSortOrder)
      } else {
        //  Sort Existing Column
        setSortOrder(sortOrder == 'desc' ? 'asc' : 'desc')
      }
    } else {
      //  Sort New Column
      setSortColumn(column)
      setSortOrder('asc')
    }
  }

  useEffect(() => {
    if (deviceId) {
      setMainDataLoading(true)
    }
  }, [deviceId])

  useEffect(() => {
    if (fetchedDetails) {
      setMainDataLoading(false)
    }
  }, [fetchedDetails])

  useEffect(() => {
    setDataLoading(true)
    di(fetchAlertsForDevices(deviceId!, DEFAULT_PAGE, DEFAULT_PAGE_SIZE, sortOrder, sortColumn))
  }, [sortOrder, sortColumn])

  useEffect(() => {
    if (deviceAlerts || (deviceAlerts && (deviceAlerts as DeviceAlertRow[]).length === 0)) {
      setDataLoading(false)
    } else if (!deviceAlerts) {
      setDataLoading(true)
    }
  }, [deviceAlerts])

  const generateOsState = (osState: string) => {
    let text = 'Latest'
    if (osState === 'SECURITY_PATCH_OUTDATED' || osState === 'OUT_OF_DATE') {
      text = 'Update Available'
    } else if (osState === 'LATEST_FOR_DEVICE') {
      text = 'End of Life'
    }
    const bgClass =
      osState === 'SECURITY_PATCH_OUTDATED' || osState === 'OUT_OF_DATE'
        ? 'bg-backgroundRed'
        : osState === 'LATEST_FOR_DEVICE'
          ? 'bg-backgroundYellow'
          : 'bg-backgroundGreen'
    const indicatorClass =
      osState === 'SECURITY_PATCH_OUTDATED' || osState === 'OUT_OF_DATE'
        ? 'bg-textRed'
        : osState === 'LATEST_FOR_DEVICE'
          ? 'bg-textYellow'
          : 'bg-Green-9'
    return (
      <div className={`rounded-md border border-mainBorder p-4 flex ${bgClass}`}>
        <div className={`h-[62px] w-1 rounded mr-4 ${indicatorClass}`} />
        <div>
          <div>
            <p className="text-sm text-mainText mb-4 font-semibold">OS Status</p>
            <p className="text-mainText text-lg font-bold">{text}</p>
          </div>
        </div>
      </div>
    )
  }

  const generateOsVersionColour = (osState: string) => {
    if (osState === 'SECURITY_PATCH_OUTDATED' || osState === 'OUT_OF_DATE') {
      return 'text-red-500'
    } else if (osState === 'LATEST_FOR_DEVICE') {
      return 'text-yellow-500'
    } else {
      return 'text-green-700'
    }
  }

  const getAlertDetails = async (alertId: number, scanId: string, alertType: AlertType) => {
    setPopoutAlertData({ alertId, scanId, alertType })
    setShowAlertPopout(true)
  }

  const handleStatusOnOff = (propertyValue: boolean | null) => {
    if (propertyValue === null) {
      return 'Unknown'
    } else if (propertyValue) {
      return 'On'
    } else {
      return 'Off'
    }
  }

  const handleStatusTrueFalse = (propertyValue: boolean | null) => {
    if (propertyValue === null) {
      return 'Unknown'
    } else if (propertyValue) {
      return 'True'
    } else {
      return 'False'
    }
  }

  const generateRiskClass = (riskScore: number) => {
    let backgroundColour = 'bg-cardFill'
    let textColour = 'bg-neutral-11'

    if (riskScore >= 0 && riskScore < 25) {
      backgroundColour = 'bg-backgroundGreen'
      textColour = 'bg-Green-9'
    } else if (riskScore >= 25 && riskScore < 75) {
      backgroundColour = 'bg-backgroundYellow'
      textColour = 'bg-textYellow'
    } else if (riskScore >= 75) {
      backgroundColour = 'bg-backgroundRed'
      textColour = 'bg-textRed'
    }

    return {
      backgroundColour,
      textColour,
    }
  }

  const generateIconColours = (value: string) => {
    if (value === 'On') {
      return 'text-green-700'
    } else if (value === 'Off') {
      return 'text-red-500'
    } else {
      return 'text-neutral-11'
    }
  }

  const generateSecurityIconColours = (value: string) => {
    if (value === 'On') {
      return 'text-red-500'
    } else if (value === 'Off') {
      return 'text-green-700'
    } else {
      return 'text-neutral-11'
    }
  }

  return (
    <>
      {!fetchedDetails && mainDataLoading && <PageSkeleton />}
      {!mainDataLoading && fetchedDetails && (
        <div className="pb-9 h-full overflow-auto">
          <div className="grid grid-cols-12 gap-4 h-[390px] mb-4 ">
            <div className="col-span-12 md:col-span-9 flex flex-col gap-4">
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                <div
                  className={`rounded-md border border-mainBorder p-4 flex ${
                    generateRiskClass(fetchedDetails?.riskScore !== null ? fetchedDetails?.riskScore : -1)
                      .backgroundColour
                  }`}
                >
                  <div
                    className={`h-[62px] w-1 rounded mr-4 ${
                      generateRiskClass(fetchedDetails?.riskScore !== null ? fetchedDetails?.riskScore : -1).textColour
                    }`}
                  />
                  <div className="w-full">
                    <div className="flex justify-between">
                      <p className="text-sm text-mainText mb-4 font-semibold">Risk Score</p>
                      <Tooltip
                        content={
                          fetchedDetails?.riskScore !== null
                            ? 'Calculated from Protection Measures (up to 60), OS Version (up to 20), CVE vulnerabilities (up to 40), and Alerts (up to 100). Lower scores reflect stronger protection, fewer vulnerabilities, and no active alerts.'
                            : 'Scores can take up to 12 hours to be generated.'
                        }
                        side="top"
                        align="end"
                        className="max-w-[400px]"
                      >
                        <IconInfoCircle
                          size={20}
                          className="text-secondaryText dark:text-neutral-8 ml-2 cursor-pointer text-mainText"
                        />
                      </Tooltip>
                    </div>
                    <p className="text-lg font-bold">
                      {fetchedDetails?.riskScore !== null ? (
                        fetchedDetails?.riskScore
                      ) : (
                        <div className="flex">
                          <p className="text-neutral-11">Unknown</p>
                        </div>
                      )}
                    </p>
                  </div>
                </div>
                <div
                  className={`rounded-md border border-mainBorder p-4 flex ${fetchedDetails?.hasOpenAlerts ? 'bg-backgroundRed' : 'bg-backgroundGreen'}`}
                >
                  <div
                    className={`h-[62px] w-1 rounded mr-4 ${fetchedDetails?.hasOpenAlerts ? 'bg-textRed' : 'bg-Green-9'}`}
                  />
                  <div>
                    <p className="text-sm text-mainText mb-4 font-semibold">Threat status</p>
                    <p className={`text-mainText text-lg font-bold`}>
                      {!fetchedDetails?.hasOpenAlerts ? 'Clear' : 'Detection'}
                    </p>
                  </div>
                </div>
                {generateOsState(fetchedDetails?.osVersionState)}
                <div className="bg-cardFill rounded-md border border-mainBorder p-4">
                  <p className="text-sm text-mainText mb-4 font-semibold">Last Check In</p>
                  <p className="text-mainText text-lg font-bold">{timeSince(fetchedDetails?.latestScanDate)}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-cardFill rounded-md border border-mainBorder p-4">
                  <p className="text-mainText mb-4 font-medium">Device</p>
                  <InfoItem
                    icon={<IconDeviceMobile size={20} className="mr-0.5" />}
                    title="Device Model"
                    value={fetchedDetails?.model || 'Unknown'}
                  />
                  <InfoItem
                    icon={
                      fetchedDetails?.platform === 'IOS' ? (
                        <IconBrandApple
                          size={20}
                          className={`mr-0.5 ${generateOsVersionColour(fetchedDetails?.osVersionState)}`}
                        />
                      ) : (
                        <IconBrandAndroid
                          size={20}
                          className={`mr-0.5 ${generateOsVersionColour(fetchedDetails?.osVersionState)}`}
                        />
                      )
                    }
                    title="OS Version"
                    value={fetchedDetails?.os || 'Unknown'}
                  />
                </div>
                <div className="bg-cardFill rounded-md border border-mainBorder p-4">
                  <p className="text-mainText mb-4 font-medium">Protection Measures</p>
                  <InfoItem
                    icon={
                      <IconMessage2Cancel
                        size={20}
                        className={`mr-0.5 ${generateIconColours(handleStatusOnOff(fetchedDetails?.smishingLastUpdatedData ?? null))}`}
                      />
                    }
                    title="Smishing Protection"
                    value={handleStatusOnOff(fetchedDetails?.smishingLastUpdatedData ?? null)}
                  />
                  <InfoItem
                    icon={
                      <IconPasswordMobilePhone
                        size={20}
                        className={`mr-0.5 ${generateIconColours(handleStatusOnOff(fetchedDetails?.screenLockLastUpdatedData ?? null))}`}
                      />
                    }
                    title="Screen Lock"
                    value={handleStatusOnOff(fetchedDetails?.screenLockLastUpdatedData ?? null)}
                  />
                  <InfoItem
                    icon={
                      <IconFaceId
                        size={20}
                        className={`mr-0.5 ${generateIconColours(handleStatusOnOff(fetchedDetails?.biometricsLastUpdatedData ?? null))}`}
                      />
                    }
                    title="Biometrics"
                    value={handleStatusOnOff(fetchedDetails?.biometricsLastUpdatedData ?? null)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 h-auto bg-cardFill rounded-md border border-mainBorder min-h-[150px] ">
                <div className="flex justify-between mx-4 mt-4 ">
                  <p className="text-mainText font-medium">Alerts</p>
                  {inPov(session) && (
                    <Button
                      onClick={() => setShowSimulateAlertModal(true)}
                      variant="outline"
                      className="cursor-pointer h-10 ml-4"
                    >
                      Simulate Alert
                    </Button>
                  )}
                </div>
                <IntelDataGrid
                  columns={deviceAlertColumns(({ alertId, scanId, alertType }) =>
                    getAlertDetails(alertId, scanId, alertType)
                  )}
                  data={deviceAlerts}
                  loading={dataLoading}
                  classNames="max-h-[400px]"
                  sortFunction={(column: string) => {
                    sortBy(column)
                  }}
                  sortColumn={sortColumn}
                  sortOrder={sortOrder}
                />
              </div>
            </div>
            <div className="col-span-12 md:col-span-3">
              {session?.portalPreferences?.vpnScanning && fetchedDetails?.eliteMonitoringTokenHash && (
                <div className="bg-cardFill rounded-md border border-mainBorder p-4 mb-4">
                  <p className="text-mainText mb-4 font-medium">Extended Protection</p>
                  <InfoItem
                    icon={<IconLineScan size={20} className="mr-0.5 text-green-700" />}
                    title="Status"
                    value="On"
                  />
                </div>
              )}
              <div className="bg-cardFill rounded-md border border-mainBorder p-4 mb-4">
                <p className="text-mainText mb-4 font-medium">App</p>
                <InfoItem
                  icon={<IconVersions size={20} className="mr-0.5 text-neutral-11 dark:text-neutral-9" />}
                  title="App Version"
                  value={fetchedDetails?.appVersionLastUpdatedData}
                />
                <InfoItem
                  icon={
                    <IconLineScan
                      size={20}
                      className={`mr-0.5 ${generateIconColours(handleStatusOnOff(fetchedDetails?.notificationLastUpdatedData ?? null))}`}
                    />
                  }
                  title="Notifications"
                  value={handleStatusOnOff(fetchedDetails?.notificationLastUpdatedData ?? null)}
                />
                <InfoItem
                  icon={<IconLockOpen2 size={20} className="mr-0.5 text-neutral-11 dark:text-neutral-9" />}
                  title="Access Code"
                  value={fetchedDetails?.accessCode}
                  showCopyButton={true}
                />
                <InfoItem
                  icon={<IconCalendarCheck size={20} className="mr-0.5 text-neutral-11 dark:text-neutral-9" />}
                  title="Added (UTC)"
                  value={formatUtcTimestamp(fetchedDetails?.createdAt, )}
                />
                <InfoItem
                  icon={<IconLineScan size={20} className="mr-0.5 text-neutral-11 dark:text-neutral-9" />}
                  title="Guides Completed"
                  value={fetchedDetails?.completedGuides}
                />
              </div>

              <div className="bg-cardFill rounded-md border border-mainBorder p-4 mb-4">
                <p className="text-mainText mb-4 font-medium">OS Security</p>
                {fetchedDetails?.platform === 'IOS' ? (
                  <>
                    <InfoItem
                      icon={
                        <IconHandStop
                          size={20}
                          className={`mr-0.5 ${generateIconColours(handleStatusOnOff(fetchedDetails?.iosLockdownUpdatedData ?? null))}`}
                        />
                      }
                      title="Lockdown Mode"
                      value={handleStatusOnOff(fetchedDetails?.iosLockdownUpdatedData ?? null)}
                    />
                  </>
                ) : (
                  <>
                    <InfoItem
                      icon={
                        <IconShield
                          size={20}
                          className={`mr-0.5 ${generateIconColours(handleStatusOnOff(fetchedDetails?.androidPlayProtectEnabledUpdatedData ?? null))}`}
                        />
                      }
                      title="Play Protect"
                      value={handleStatusOnOff(fetchedDetails?.androidPlayProtectEnabledUpdatedData ?? null)}
                    />
                    <InfoItem
                      icon={
                        <IconUsb
                          size={20}
                          className={`mr-0.5 ${generateSecurityIconColours(handleStatusOnOff(fetchedDetails?.androidAdbEnabledUpdatedData ?? null))}`}
                        />
                      }
                      title="Debug Bridge"
                      value={handleStatusOnOff(fetchedDetails?.androidAdbEnabledUpdatedData ?? null)}
                    />
                    <InfoItem
                      icon={<IconCode size={20} className={`mr-0.5 text-neutral-11`} />}
                      title="Developer Mode"
                      value={handleStatusOnOff(fetchedDetails?.androidDeveloperModeEnabledUpdatedData ?? null)}
                    />
                    <InfoItem
                      icon={
                        <IconAlertHexagon
                          size={20}
                          className={`mr-0.5 ${generateSecurityIconColours(handleStatusOnOff(fetchedDetails?.androidRootDetectedUpdatedData ?? null))}`}
                        />
                      }
                      title="Root Detection"
                      value={handleStatusTrueFalse(fetchedDetails?.androidRootDetectedUpdatedData ?? null)}
                    />
                    <InfoItem
                      icon={
                        <IconDownload
                          size={20}
                          className={`mr-0.5 ${generateSecurityIconColours(handleStatusOnOff(fetchedDetails?.androidSideloadingEnabledUpdatedData ?? null))}`}
                        />
                      }
                      title="Side Loading"
                      value={handleStatusOnOff(fetchedDetails?.androidSideloadingEnabledUpdatedData ?? null)}
                    />
                  </>
                )}
              </div>

              <div className="bg-cardFill rounded-md border border-mainBorder p-4 mb-4">
                <p className="text-mainText mb-4 font-medium">Member</p>
                <InfoItem
                  icon={<IconUser size={20} className="mr-0.5 text-neutral-11 dark:text-neutral-9" />}
                  title="Name"
                  value={fetchedDetails?.name}
                />
              </div>

              {fetchedDetails?.mdmPayload && (
                <div className="bg-cardFill rounded-md border border-mainBorder p-4 mb-4">
                  <p className="text-mainText mb-4 font-medium">MDM Payload</p>
                  <textarea
                    className="w-full h-28 resize-none overflow-auto bg-neutral-2 rounded-lg border border-mainBorder p-2"
                    readOnly
                    value={fetchedDetails?.mdmPayload ?? ''}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {!mainDataLoading && !fetchedDetails && (
        <div className="flex flex-col items-center justify-center h-full">
          <p className="text-mainText text-lg font-bold">No Device Found</p>
          <p className="text-neutral-11 text-sm">Please check the device ID and try again.</p>
          <Button
            className="mt-4 text-center flex bg-black mx-auto cursor-pointer"
            onClick={() => {
              navigate('/devices')
            }}
          >
            Return to Devices
          </Button>
        </div>
      )}
    </>
  )
}

export default DeviceOverviewSummary
